import { useMemo } from "react";
import { useReactTable, getCoreRowModel, getExpandedRowModel, type ColumnDef, type SortingState, type VisibilityState } from "@tanstack/react-table";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Checkbox } from "../../../../ui/checkbox";
import Button from "../../../Button";
import { useUrlSort } from "./useUrlSort";
import { useUrlPagination } from "./useUrlPagination";
import "../types";

const EMPTY_DATA: never[] = [];

interface PaginationConfig {
    defaultPageSize?: number;
    showSizeChanger?: boolean;
}

interface UseTableProps<TData> {
    data: TData[] | undefined;
    columns: ColumnDef<TData, any>[];
    loading?: boolean;
    totalItems?: number;
    errors?: Record<string, string>;
    enableRowSelection?: boolean;
    enableExpanding?: boolean;
    enableSorting?: boolean;
    footer?: boolean;
    pagination?: boolean | PaginationConfig;
    defaultSort?: SortingState;
    defaultColumnVisibility?: VisibilityState;
    defaultFilter?: string;
    onRowClick?: (row: TData) => void;
    // Merged into the table's own meta object, alongside loading/totalItems/errors/etc.,
    // so consumers can thread extra data (e.g. row-update callbacks) through to cells.
    meta?: Record<string, any>;
}

export const useTable = <TData,>({
    data = EMPTY_DATA,
    columns,
    loading,
    totalItems,
    errors,
    enableRowSelection = true,
    enableExpanding = false,
    enableSorting = true,
    pagination = {
        defaultPageSize: 20,
        showSizeChanger: true
    },
    footer = false,
    defaultSort,
    defaultColumnVisibility,
    defaultFilter,
    onRowClick,
    meta,
}: UseTableProps<TData>) => {
    const enablePagination = pagination !== false;
    const defaultPageSize = (typeof pagination === "object" && pagination.defaultPageSize) || 20;
    const showSizeChanger = typeof pagination === "object" ? pagination.showSizeChanger ?? true : true;

    const [sorting, onSortingChange] = useUrlSort(defaultSort);
    const { pageIndex, pageSize, onPageChange, onPageSizeChange } = useUrlPagination(defaultPageSize);

    const allColumns = useMemo<ColumnDef<TData, any>[]>(() => {
        let result = columns;

        if (enableExpanding) {
            const expandColumn: ColumnDef<TData, any> = {
                id: "expand",
                enableSorting: false,
                enableHiding: false,
                header: ({ table }) => (
                    <Button variant="ghost" size="icon" onClick={table.getToggleAllRowsExpandedHandler()}>
                        {table.getIsAllRowsExpanded() ? <ChevronDown className="tw-h-4 tw-w-4" /> : <ChevronRight className="tw-h-4 tw-w-4" />}
                    </Button>
                ),
                cell: ({ row }) => row.getCanExpand() ? (
                    <Button variant="ghost" size="icon" onClick={row.getToggleExpandedHandler()}>
                        {row.getIsExpanded() ? <ChevronDown className="tw-h-4 tw-w-4" /> : <ChevronRight className="tw-h-4 tw-w-4" />}
                    </Button>
                ) : null,
            };

            result = [expandColumn, ...result];
        }

        if (enableRowSelection) {
            const selectColumn: ColumnDef<TData, any> = {
                id: "select",
                enableSorting: false,
                enableHiding: false,
                header: ({ table }) => (
                    <Checkbox
                        checked={table.getIsAllPageRowsSelected() ? true : table.getIsSomePageRowsSelected() ? "indeterminate" : false}
                        onCheckedChange={(value) => table.toggleAllPageRowsSelected(value === true)}
                        onClick={(e) => e.stopPropagation()}
                    />
                ),
                cell: ({ row }) => (
                    <Checkbox
                        checked={row.getIsSelected()}
                        onCheckedChange={(value) => row.toggleSelected(value === true)}
                        onClick={(e) => e.stopPropagation()}
                    />
                ),
            };

            result = [selectColumn, ...result];
        }

        return result;
    }, [columns, enableRowSelection, enableExpanding]);

    const paginationProps = useMemo(() => {
        if (!enablePagination) return {};
        return { pageIndex, pageSize, onPageChange, onPageSizeChange, showSizeChanger };
    }, [enablePagination, onPageChange, onPageSizeChange, pageIndex, pageSize, showSizeChanger]);

    return useReactTable({
        data,
        columns: allColumns,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getRowCanExpand: () => true,
        enableRowSelection,
        enableSorting,
        initialState: {
            columnVisibility: defaultColumnVisibility,
        },
        state: { sorting },
        onSortingChange,
        meta: {
            loading,
            totalItems,
            errors,
            footer,
            defaultFilter,
            onRowClick,
            ...paginationProps,
            ...meta,
        },
    });
};
